import Favico from 'favico.js-slevomat';
import log from '@converse/headless/log';
import { __ } from 'i18n';
import { _converse, api, converse } from '@converse/headless/core.js';
import { isEmptyMessage } from '@converse/headless/utils/core.js';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

const { Strophe } = converse.env;

converse.env.Favico = Favico;

let favicon;

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i;
const VIDEO_EXTENSIONS = /\.(mp4|mov|webm|avi|mkv|m4v|3gp)(\?.*)?$/i;

function getMessageMediaType (attrs) {
    // check explicit MIME type first if available (e.g. from upload metadata)
    const mimeType = attrs.file?.type || attrs.media_type;

    if (mimeType) {
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (attrs.oob_url || attrs.file) return 'file';
    }

    // fall back to extension checks on url/body
    const urlToCheck = attrs.oob_url || attrs.body;
    if (urlToCheck) {
        const trimmed = urlToCheck.trim();
        if (IMAGE_EXTENSIONS.test(trimmed)) return 'image';
        if (VIDEO_EXTENSIONS.test(trimmed)) return 'video';
    }

    // if there's an oob_url or file attachment but it didn't match
    // image/video, treat it as a generic file
    if (attrs.oob_url || attrs.file) return 'file';

    return null;
}

export function isMessageToHiddenChat (attrs) {
    return _converse.isTestEnv() || (_converse.chatboxes.get(attrs.from)?.isHidden() ?? false);
}

export function areDesktopNotificationsEnabled () {
    try {
        return _converse.isTestEnv() || (
            api.settings.get('show_desktop_notifications') &&
            Notification.permission === 'granted'
        );
    } catch {
        return false
    }
}

export function clearFavicon () {
    favicon?.badge(0);
    navigator.clearAppBadge?.()
        .catch(e => log.error("Could not clear unread count in app badge " + e));
}

// export function updateUnreadFavicon () {
//     if (api.settings.get('show_tab_notifications')) {
//         favicon = favicon ?? new converse.env.Favico({ type: 'circle', animation: 'pop' });
//         const chats = _converse.chatboxes.models;
//         const num_unread = chats.reduce((acc, chat) => acc + (chat.get('num_unread') || 0), 0);
//         favicon.badge(num_unread);
//         navigator.setAppBadge?.(num_unread)
//             .catch(e => log.error("Could set unread count in app badge - " + e));
//     }
// }


function isReferenced (references, muc_jid, nick) {
    const check = r => [_converse.bare_jid, `${muc_jid}/${nick}`].includes(r.uri.replace(/^xmpp:/, ''));
    return references.reduce((acc, r) => acc || check(r), false);
}


/**
 * Is this a group message for which we should notify the user?
 * @private
 * @param { MUCMessageAttributes } attrs
 */
export async function shouldNotifyOfGroupMessage (attrs) {
    if (!attrs?.body && !attrs?.message) {
        // attrs.message is used by 'info' messages
        return false;
    }
    const jid = attrs.from;
    const muc_jid = attrs.from_muc;
    const notify_all = api.settings.get('notify_all_room_messages');
    const room = _converse.chatboxes.get(muc_jid);
    const resource = Strophe.getResourceFromJid(jid);
    const sender = (resource && Strophe.unescapeNode(resource)) || '';
    let is_mentioned = false;
    const nick = room.get('nick');

    if (api.settings.get('notify_nicknames_without_references')) {
        is_mentioned = new RegExp(`\\b${nick}\\b`).test(attrs.body);
    }

    const is_not_mine = sender !== nick;
    const should_notify_user =
        notify_all === true ||
        (Array.isArray(notify_all) && notify_all.includes(muc_jid)) ||
        isReferenced(attrs.references, muc_jid, nick) ||
        is_mentioned;

    if (is_not_mine && !!should_notify_user) {
        /**
         * *Hook* which allows plugins to run further logic to determine
         * whether a notification should be sent out for this message.
         * @event _converse#shouldNotifyOfGroupMessage
         * @example
         *  api.listen.on('shouldNotifyOfGroupMessage', (should_notify) => {
         *      return should_notify && flurb === floob;
         *  });
         */
        const should_notify = await api.hook('shouldNotifyOfGroupMessage', attrs, true);
        return should_notify;
    }
    return false;
}

async function shouldNotifyOfInfoMessage (attrs) {
    if (!attrs.from_muc) {
        return false;
    }
    const room = await api.rooms.get(attrs.from_muc);
    if (!room) {
        return false;
    }
    const nick = room.get('nick');
    const muc_jid = attrs.from_muc;
    const notify_all = api.settings.get('notify_all_room_messages');
    return (
        notify_all === true ||
        (Array.isArray(notify_all) && notify_all.includes(muc_jid)) ||
        isReferenced(attrs.references, muc_jid, nick)
    );
}

/**
 * @private
 * @async
 * @method shouldNotifyOfMessage
 * @param { MessageData|MUCMessageData } data
 */
function shouldNotifyOfMessage (data) {
    const { attrs } = data;
    if (!attrs || attrs.is_forwarded) {
        return false;
    }

    if (attrs.is_delayed && Capacitor.isNativePlatform()) {
        return false;
    }

    if (attrs['type'] === 'groupchat') {
        return shouldNotifyOfGroupMessage(attrs);
    } else if (attrs['type'] === 'info') {
        return shouldNotifyOfInfoMessage(attrs);
    } else if (attrs.is_headline) {
        // We want to show notifications for headline messages.
        return isMessageToHiddenChat(attrs);
    }
    const is_me = Strophe.getBareJidFromJid(attrs.from) === _converse.bare_jid;
    return (
        !isEmptyMessage(attrs) &&
        !is_me &&
        (api.settings.get('show_desktop_notifications') === 'all' || isMessageToHiddenChat(attrs))
    );
}

export function showFeedbackNotification (data) {
    if (data.klass === 'error' || data.klass === 'warn') {
        const n = new Notification(data.subject, {
            body: data.message,
            lang: _converse.locale,
            icon: api.settings.get('notification_icon')
        });
        setTimeout(n.close.bind(n), 5000);
    }
}

/**
 * Creates a Notification to inform of a change in a
 * contact's chat state
 * via Service Workers or Capacitor's Local Notification
 */
function showChatStateNotification (contact) {
    if (api.settings.get('chatstate_notification_blacklist')?.includes(contact.jid)) {
        // Don't notify if the user is being ignored.
        return;
    }
    const chat_state = contact.presence.get('show');
    let message = null;
    if (chat_state === 'offline') {
        message = __('has gone offline');
    } else if (chat_state === 'away') {
        message = __('has gone away');
    } else if (chat_state === 'dnd') {
        message = __('is busy');
    } else if (chat_state === 'online') {
        message = __('has come online');
    }
    if (message === null) {
        return;
    }
    const n = new Notification(contact.getDisplayName(), {
        body: message,
        lang: _converse.locale,
        icon: api.settings.get('notification_icon')
    });
    setTimeout(() => n.close(), 5000);
}


/**
 * Shows a Notification with the passed in message
 * via Service Workers or Capacitor's Local Notification
 * @private
 * @param { MessageData|MUCMessageData } data
 */
async function showMessageNotification (data) {
    const { attrs } = data;
    if (attrs.is_error) {
        return;
    }

    if (!Capacitor.isNativePlatform() && !areDesktopNotificationsEnabled()) {
        return;
    }

    let title, roster_item;
    const full_from_jid = attrs.from;
    const from_jid = Strophe.getBareJidFromJid(full_from_jid);
    const mediaType = getMessageMediaType(attrs);

    if (attrs.type == 'info') {
        title = attrs.message;
    } else if (attrs.type === 'headline') {
        if (!from_jid.includes('@') || api.settings.get('allow_non_roster_messaging')) {
            title = __('Notification from %1$s', from_jid);
        } else {
            return;
        }
    } else if (!from_jid.includes('@')) {
        // workaround for Prosody which doesn't give type "headline"
        title = __('Notification from %1$s', from_jid);
    } else if (attrs.type === 'groupchat') {
        title = __('%1$s says', Strophe.getResourceFromJid(full_from_jid));
    } else {
        if (_converse.roster === undefined) {
            log.error('Could not send notification, because roster is undefined');
            return;
        }
        roster_item = _converse.roster.get(from_jid);
        if (roster_item !== undefined) {
            title = mediaType
                ? __('%1$s', roster_item.getDisplayName())
                : __('%1$s says', roster_item.getDisplayName())
        } else {
            if (api.settings.get('allow_non_roster_messaging')) {
                title = __('%1$s says', from_jid);
            } else {
                return;
            }
        }
    }

    let body;
    if (attrs.type == 'info') {
        body = attrs.reason;
    } else if (mediaType === 'image') {
        body = __('sent an image');
    } else if (mediaType === 'video') {
        body = __('sent a video');
    } else if (mediaType === 'file') {
        body = __('sent a file');
    } else {
        body = attrs.is_encrypted ? attrs.plaintext : attrs.body;
        if (!body) {
            return;
        }
    }

    const userImageUrl = `${backendUrl}/preview-image-id/profile/${from_jid.split('-')[0]}`

    if (Capacitor.isNativePlatform()) {
        if (Capacitor.getPlatform() === 'android') {
            await LocalNotifications.createChannel({
                id: 'messages',
                name: 'Messages',
                importance: 5,
                visibility: 1,
                sound: 'default',
                vibration: true,
            });
        }

        await LocalNotifications.schedule({
            notifications: [{
                title,
                body,
                channelId: 'messages',
                id: Math.floor(Math.random() * 2147483647),
                extra: { from_jid },
                attachments: [{ id: 'sender-avatar', url: userImageUrl }]
            }]
        });
    } else {
        try {
            const reg = await navigator.serviceWorker.ready;

            await reg.showNotification(title, {
                'body': body,
                'lang': _converse.locale,
                'icon': userImageUrl || api.settings.get('notification_icon'),
                'tag': `${from_jid}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                'data': { from_jid }
            });
        } catch (err) {
            console.error(err)

            const n = new Notification(title, {
                'body': body,
                'lang': _converse.locale,
                'icon': userImageUrl || api.settings.get('notification_icon'),
                'requireInteraction': !api.settings.get('notification_delay')
            });
            if (api.settings.get('notification_delay')) {
                setTimeout(() => n.close(), api.settings.get('notification_delay'));
            }
            n.onclick = function (event) {
                event.preventDefault();
                window.focus();
                const chat = _converse.chatboxes.get(from_jid);
                chat.maybeShow(true);
            }
        }
    }
}

/**
 * playSoundNotification
 * @returns Promise
 */
function playSoundNotification () {
    if (api.settings.get('play_sounds') && window.Audio !== undefined) {
        const audioOgg = new Audio(api.settings.get('sounds_path') + 'msg_received.ogg');
        const canPlayOgg = audioOgg.canPlayType('audio/ogg');
        if (canPlayOgg === 'probably') {
            return audioOgg.play();
        }
        const audioMp3 = new Audio(api.settings.get('sounds_path') + 'msg_received.mp3');
        const canPlayMp3 = audioMp3.canPlayType('audio/mp3');
        if (canPlayMp3 === 'probably') {
            return audioMp3.play();
        } else if (canPlayOgg === 'maybe') {
            return audioOgg.play();
        } else if (canPlayMp3 === 'maybe') {
            return audioMp3.play();
        }
    }
}

/**
 * Event handler for the on('message') event. Will call methods
 * to play sounds and show notifications
 * via Service Workers or Capacitor's Local Notification.
 */
export async function handleMessageNotification (data) {
    if (!await shouldNotifyOfMessage(data)) {
        return false;
    }
    /**
     * Triggered when a notification (sound or notification) for a new
     * message has will be made.
     * @event _converse#messageNotification
     * @type { MessageData|MUCMessageData}
     * @example _converse.api.listen.on('messageNotification', data => { ... });
     */
    api.trigger('messageNotification', data);
    const promise = playSoundNotification();
    if (promise !== undefined) {
      promise.then(_ => {
        // Autoplay started!
      }).catch(error => {
        // Autoplay was prevented.
        // more details see here https://developer.chrome.com/blog/autoplay/
      });
    }
    await showMessageNotification(data);
}

export function handleFeedback (data) {
    if (areDesktopNotificationsEnabled(true)) {
        showFeedbackNotification(data);
    }
}

/**
 * Event handler for on('contactPresenceChanged').
 * Will show a notification to indicate that the chat status has changed
 * via Service Workers or Capacitor's Local Notification.
 */
export function handleChatStateNotification (contact) {
    if (areDesktopNotificationsEnabled() && api.settings.get('show_chat_state_notifications')) {
        // showChatStateNotification(contact);
    }
}

function showContactRequestNotification (contact) {
    const n = new Notification(contact.getDisplayName(), {
        body: __('wants to be your contact'),
        lang: _converse.locale,
        icon: api.settings.get('notification_icon')
    });
    setTimeout(() => n.close(), 5000);
}

export function handleContactRequestNotification (contact) {
    if (areDesktopNotificationsEnabled(true)) {
        showContactRequestNotification(contact);
    }
}

export async function requestPermission () {
    if (Capacitor.isNativePlatform()) {
        const { display } = await LocalNotifications.checkPermissions();

        if (!['denied', 'granted'].includes(display)) {
            await LocalNotifications.requestPermissions();
        }
    } else {
        try {
            if (!['denied', 'granted'].includes(Notification.permission)) {
                Notification.requestPermission();
            }
        } catch {
            //
        }
    }
}
